import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAdminAppearance } from '../../contexts/AdminAppearanceContext';
import { isNoDyeProblem } from '../../utils/problemTypeDisplay';
import { useBlinkingProblemTypes } from '../../utils/blinkingProblemTypes';
import { formatKg, isSectionHeaderRow, parseQty, parseWeightKg, sectionTotalWeightsKg } from '../../utils/productionKg';

// Stronger shade a pulsing row moves towards, keyed by the row's own colour.
// A row reported as incomplete is red when it carries notes and yellow otherwise,
// so each keeps its meaning while pulsing.
const ROW_PULSE_COLORS: Record<string, string> = {
  '#fff3cd': '#fcd34d', // żółty → mocniejszy amber
  '#f8d7da': '#f1a7b0', // czerwony → mocniejsza czerwień
};

interface ProductionItem {
  id: number;
  linia: string | null;
  tydzien: string | null;
  material: string | null;
  srednica: string | null;
  waga: string | null;
  szpula: string | null;
  kolor: string | null;
  barwnik: string | null;
  barwnik_dostarczony?: boolean;
  /** 'available' | 'missing' | null — dye availability marked by a person. */
  barwnik_status?: 'available' | 'missing' | null;
  rewind_entries?: Array<{ id: number; przewinieta: boolean; marked_to_rewind?: boolean }>;
  doz_procent: string | null;
  marka: string | null;
  nazwa_oryg: string | null;
  indeks_kat: string | null;
  do_produkcji: string | null;
  ean: string | null;
  zmiana_1: string | null;
  zmiana_2: string | null;
  /** Counted from weighed big-spool labels: zmiana_1 / zmiana_2 hold the whole pieces of the label weights. */
  weighed_spools?: boolean;
  uwagi: string | null;
  priorytet?: boolean;
  pivot?: {
    status: 'pending' | 'in_progress' | 'completed' | 'problem_bb' | 'problem_awaria' | 'niepelna_produkcja';
    notes: string | null;
    wykonano_ilosc?: number | null;
    user_id?: number | null;
    started_at?: string | null;
  };
}

interface ProductionTableProps {
  items: ProductionItem[];
  onRowClick: (item: ProductionItem) => void;
  onNotesClick: (e: React.MouseEvent, item: ProductionItem) => void;
  onQuantityClick: (e: React.MouseEvent, item: ProductionItem, field: 'zmiana_1' | 'zmiana_2') => void;
  onBarwnikClick: (e: React.MouseEvent, item: ProductionItem) => void;
  getRowColor: (item: ProductionItem) => string;
  getItemStatus: (item: ProductionItem) => string;
  /** Overrides the TO PRODUCE / PRODUCED column labels (e.g. warehouse: To rewind / Rewound) */
  labelOverrides?: { toProduce?: string; produced?: string };
  /** Hides the last notes column (📋) — used in the rewinding station view */
  hideNotesColumn?: boolean;
  /** Shows the production line as the first column — used in the rewinding station view */
  showLineColumn?: boolean;
  /** Shows a narrow priority (⭐) column — used in the rewinding station view */
  showPriorityColumn?: boolean;
  /** Hides the Z1/Z2 quantity-entry columns — used on big-spool lines, where the
   *  produced quantity is entered at the rewinding station, not in the line panel. */
  hideQuantityColumns?: boolean;
  /** Adds the TOTAL WEIGHT column (TO PRODUCE x per-piece weight, in kg) — used in "My production". */
  showTotalWeightColumn?: boolean;
  /** Adds REWINDER, REWOUND BY and TRANSITION columns (item fields `rewinders_text`, `rewound_by_text`, `transition_text`) — rewinding station view. */
  showRewoundByColumns?: boolean;
  /** Hides the DYE and DOSAGE % columns — rewinding station view. */
  hideDyeColumns?: boolean;
  /** Hides the EAN column — rewinding station view. */
  hideEanColumn?: boolean;
}

const ProductionTable: React.FC<ProductionTableProps> = ({
  items,
  onRowClick,
  onNotesClick,
  onQuantityClick,
  onBarwnikClick,
  getRowColor,
  getItemStatus,
  labelOverrides,
  hideNotesColumn,
  showLineColumn,
  showPriorityColumn,
  hideQuantityColumns,
  showTotalWeightColumn,
  showRewoundByColumns,
  hideDyeColumns,
  hideEanColumn,
}) => {
  const { t } = useLanguage();
  const { columnWidthLeft } = useAdminAppearance();
  const isBlinkingNote = useBlinkingProblemTypes();

  const narrowColumnWidth = 32; // Wąska kolumna dla uwag
  const smallColumnWidth = 48; // Węższe kolumny Z1, Z2
  const wideColumnWidth = 78; // DO PRODUKCJI
  const mediumColumnWidth = 65; // POZO-STAŁO
  const mediumNarrowWidth = 60; // WYPROD
  const narrowLeftWidth = 55; // Wąskie lewe kolumny: Waga, DOZ. %, Szpula, Średnica

  // Column definitions for scrollable (left) columns
  const scrollableColumns = [
    ...(showPriorityColumn ? [{ key: 'priorytet', label: '⭐', bold: false, color: '', width: 34 }] : []),
    ...(showLineColumn ? [{ key: 'linia', label: t('table.line').toUpperCase(), bold: true, color: '', width: 50 }] : []),
    { key: 'material', label: t('table.material'), bold: false, color: '' },
    { key: 'srednica', label: t('table.diameter').toUpperCase(), bold: true, color: '', width: 80 },
    { key: 'waga', label: t('table.weight').toUpperCase(), bold: false, color: '', narrow: true },
    { key: 'szpula', label: t('table.spool').toUpperCase(), bold: false, color: '' },
    { key: 'kolor', label: t('table.color').toUpperCase(), bold: false, color: '' },
    ...(hideDyeColumns ? [] : [
      { key: 'barwnik', label: t('table.dye').toUpperCase(), bold: false, color: '' },
      { key: 'doz_procent', label: t('table.dosagePercent').toUpperCase(), bold: true, color: '', width: 70, fontSize: 16 },
    ]),
    { key: 'marka', label: t('table.brand').toUpperCase(), bold: false, color: '' },
    { key: 'nazwa_oryg', label: t('table.originalName').toUpperCase(), bold: false, color: '', wrap: true },
    { key: 'indeks_kat', label: t('table.catalogIndex').toUpperCase(), bold: false, color: '' },
    // Only show EAN column if any item has an EAN value
    ...(!hideEanColumn && items.some(item => item.ean) ? [{ key: 'ean', label: 'EAN', bold: false, color: '' }] : []),
    ...(showRewoundByColumns ? [
      { key: 'rewinders_text', label: t('table.rewinder').toUpperCase(), bold: false, color: '', wrap: true },
      { key: 'rewound_by_text', label: t('table.rewoundBy').toUpperCase(), bold: false, color: '', wrap: true },
      { key: 'transition_text', label: t('table.transition').toUpperCase(), bold: false, color: '', wrap: true },
    ] : []),
  ];

  const totalWeightColumnWidth = 92; // ŁĄCZNA WAGA — mieści dwuwierszowy nagłówek i wartość typu "1 250,00"

  const getLeftWidth = (col: { narrow?: boolean; width?: number }) => col.width ?? (col.narrow ? narrowLeftWidth : columnWidthLeft);
  const leftColumnsTotal = scrollableColumns.reduce((sum, col) => sum + getLeftWidth(col), 0);

  // Sticky column definitions (right), in left-to-right order. Columns can be dropped
  // (Z1/Z2, notes) or added (total weight) — offsets below are derived from this list,
  // so nothing has to be renumbered when the set changes.
  const stickyColumns = [
    { key: 'do_produkcji', label: (labelOverrides?.toProduce ?? t('table.toProduce')).toUpperCase(), width: wideColumnWidth, bgColor: '#fbbf24', color: '#000', borderLeft: true },
    ...(showTotalWeightColumn ? [{ key: 'total_weight', label: t('table.totalWeight').toUpperCase(), width: totalWeightColumnWidth, bgColor: '#fde68a', color: '#000', borderLeft: false, wrapHeader: true }] : []),
    { key: 'wyprodukowano', label: labelOverrides?.produced ?? t('myProduction.produced'), width: mediumNarrowWidth, bgColor: '#e5e7eb', color: '#000', borderLeft: false },
    { key: 'pozostalo', label: t('myProduction.remaining'), width: mediumColumnWidth, bgColor: '#3b82f6', color: 'white', borderLeft: false },
    ...(hideQuantityColumns ? [] : [
      { key: 'zmiana_1', label: t('table.z1'), width: smallColumnWidth, bgColor: '#f8f9fa', color: '#000', borderLeft: false },
      { key: 'zmiana_2', label: t('table.z2'), width: smallColumnWidth, bgColor: '#f8f9fa', color: '#000', borderLeft: false },
    ]),
    ...(hideNotesColumn ? [] : [{ key: 'uwagi', label: '📋', width: narrowColumnWidth, bgColor: '#f8f9fa', color: '#000', borderLeft: false }]),
  ];

  const stickyTotal = stickyColumns.reduce((sum, col) => sum + col.width, 0);
  const tableWidth = leftColumnsTotal + stickyTotal;

  // right offset of a sticky column = combined width of the columns after it
  const stickyRight: Record<string, number> = {};
  stickyColumns.reduceRight((acc, col) => {
    stickyRight[col.key] = acc;
    return acc + col.width;
  }, 0);
  const stickyWidth: Record<string, number> = Object.fromEntries(stickyColumns.map((col) => [col.key, col.width]));

  const commonCellStyle = {
    padding: '10px',
    border: '1px solid #dee2e6',
    fontSize: '10px',
  };

  const scrollableCellStyle = (col: { narrow?: boolean }) => {
    const w = getLeftWidth(col);
    return {
      ...commonCellStyle,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const,
      width: w,
      minWidth: w,
      maxWidth: w,
    };
  };

  const stickyCellStyle = (key: string, bgColor: string, borderLeft: boolean = false) => {
    const w = stickyWidth[key];
    return {
      ...commonCellStyle,
      padding: '5px 4px',
      position: 'sticky' as const,
      right: stickyRight[key],
      zIndex: 1,
      backgroundColor: bgColor,
      width: w,
      minWidth: w,
      maxWidth: w,
      boxSizing: 'border-box' as const,
      overflow: 'hidden',
      ...(borderLeft && { borderLeft: '3px solid #000' }),
    };
  };

  return (
    <div style={{ width: '100%', marginTop: '20px', overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
      <table
        style={{
          width: '100%',
          minWidth: `${tableWidth}px`,
          tableLayout: 'fixed',
          borderCollapse: 'collapse',
          backgroundColor: 'white',
          fontSize: '12px',
        }}
      >
        <colgroup>
          {scrollableColumns.map((col, idx) => (
            <col key={`scroll-col-${idx}`} style={{ width: `${getLeftWidth(col)}px` }} />
          ))}
          {stickyColumns.map((col, idx) => (
            <col key={`sticky-col-${idx}`} style={{ width: `${col.width}px` }} />
          ))}
        </colgroup>

        <thead>
          <tr style={{ borderBottom: '2px solid #dee2e6' }}>
            {/* Scrollable column headers */}
            {scrollableColumns.map((col) => {
              const w = getLeftWidth(col);
              return (
                <th
                  key={col.key}
                  style={{
                    ...commonCellStyle,
                    textAlign: 'left',
                    wordBreak: 'break-word',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: w,
                    minWidth: w,
                    maxWidth: w,
                    position: 'sticky' as const,
                    top: 0,
                    zIndex: 3,
                    backgroundColor: '#f8f9fa',
                    boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)',
                  }}
                >
                  {col.label}
                </th>
              );
            })}

            {/* Sticky column headers (sticky both top AND right) */}
            {stickyColumns.map((col) => {
              const w = col.width;
              return (
                <th
                  key={col.key}
                  style={{
                    ...commonCellStyle,
                    textAlign: 'center' as const,
                    position: 'sticky' as const,
                    top: 0,
                    right: stickyRight[col.key],
                    zIndex: 4,
                    backgroundColor: col.bgColor,
                    color: col.color,
                    width: w,
                    minWidth: w,
                    maxWidth: w,
                    boxSizing: 'border-box' as const,
                    overflow: 'hidden',
                    // Dłuższe nagłówki (ŁĄCZNA WAGA) łamią się na dwie linie zamiast
                    // zostać uciętymi wielokropkiem.
                    ...((col as any).wrapHeader
                      ? { whiteSpace: 'normal' as const, wordBreak: 'break-word' as const, lineHeight: 1.15 }
                      : { textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }),
                    padding: '5px 4px',
                    boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)',
                    ...(col.borderLeft && { borderLeft: '3px solid #000' }),
                  }}
                >
                  {col.label}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {(() => {
            const totalCols = scrollableColumns.length + stickyColumns.length;
            const rows: React.ReactNode[] = [];
            const sectionWeights = sectionTotalWeightsKg(items);

            items.forEach((item) => {
              const assignmentId = (item as any).assignmentId || 0;

              // Header detection: rows from Excel where production fields are empty
              if (isSectionHeaderRow(item)) {
                const headerText = item.linia || item.material || item.nazwa_oryg || item.indeks_kat || '';
                if (headerText) {
                  rows.push(
                    <tr
                      key={`header-${item.id}`}
                      style={{
                        borderBottom: '2px solid #000',
                        backgroundColor: '#cbd5e1',
                      }}
                    >
                      <td
                        colSpan={totalCols}
                        style={{
                          ...commonCellStyle,
                          fontWeight: 'bold',
                          fontSize: '13px',
                          color: '#1e293b',
                          padding: '10px 14px',
                          letterSpacing: '0.5px',
                        }}
                      >
                        {headerText}
                        {` - ${t('table.sectionTotalWeight', { kg: formatKg(sectionWeights.get(item.id) ?? 0) })}`}
                      </td>
                    </tr>
                  );
                }
                return;
              }

            const rowColor = getRowColor(item);
            const status = getItemStatus(item);
            // Pozycja przewinięta (brak oczekujących, jest przewinięty wpis) wygląda jak zakończona.
            const rewindEntries = (item as any).rewind_entries || [];
            const pendingRewind = rewindEntries.some((e: any) => !e.przewinieta);
            const doneRewind = !pendingRewind && rewindEntries.some((e: any) => e.przewinieta);
            const completedLook = status === 'completed' || doneRewind;
            const locked = completedLook;
            const doProdukcji = item.do_produkcji ? parseInt(item.do_produkcji) : 0;
            const zmiana1 = item.zmiana_1 ? parseInt(item.zmiana_1) : 0;
            const zmiana2 = item.zmiana_2 ? parseInt(item.zmiana_2) : 0;
            const wyprodukowano = zmiana1 + zmiana2;
            const pozostalo = Math.max(0, doProdukcji - wyprodukowano);
            // Weighed big spools get their pieces from the printed labels (spool weighing window).
            const quantityTitle = (fallback: string) =>
              locked ? t('myProduction.cannotEditCompleted') : item.weighed_spools ? t('spoolLabel.clickToWeigh') : fallback;
            // "Brak barwnika" – świeci się tylko komórka w kolumnie Barwnik
            const dyeProblem = isNoDyeProblem(item.pivot?.status, item.pivot?.notes || item.uwagi);
            // Wiersz jest "gotowy", gdy getRowColor zwrócił zielony (#d4edda) — czyli w pełni
            // wyprodukowane / zakończone / przewinięte. Wtedy żadna uwaga nie powinna migać
            // ani zmieniać koloru: pozycja ma być po prostu zielona.
            const isDone = rowColor === '#d4edda';
            // Deficyt – pozycja zgłoszona jako niepełna produkcja miga, dopóki nie zostanie
            // dopełniona. Po dopełnieniu getRowColor zwraca zielony i miganie znika.
            const deficit = status === 'niepelna_produkcja' && wyprodukowano < doProdukcji && !isDone;
            // Miganie z uwagi – pozycja miga, gdy wybrana uwaga ma włączoną opcję migania,
            // ale TYLKO dopóki nie jest gotowa. Po wypełnieniu/zakończeniu wiersz jest zielony i nie miga.
            const blinkingNote = !deficit && !isDone && isBlinkingNote(item.pivot?.notes || item.uwagi);
            const pulseColor = ROW_PULSE_COLORS[rowColor] ?? '#fcd34d';

            rows.push(
              <tr
                key={item.id}
                className={deficit ? 'deficit-row-warning' : blinkingNote ? 'blink-row-warning' : undefined}
                onClick={() => { onRowClick(item); }}
                style={{
                  borderBottom: '2px solid #000',
                  backgroundColor: rowColor,
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  ...(deficit && {
                    ['--deficit-base' as any]: rowColor,
                    ['--deficit-pulse' as any]: pulseColor,
                  }),
                  ...(blinkingNote && {
                    ['--blink-base' as any]: rowColor,
                    ['--blink-pulse' as any]: pulseColor,
                  }),
                }}
                onMouseEnter={(e) => {
                  if (e.currentTarget.style.backgroundColor !== rowColor) {
                    e.currentTarget.style.backgroundColor = '#e9ecef';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = rowColor;
                }}
              >
                {/* Scrollable data cells */}
                {scrollableColumns.map((col) => {
                  // Priorytet – gwiazdka zamiast wartości, pusta komórka gdy brak priorytetu.
                  const isPriority = col.key === 'priorytet';
                  const value = isPriority
                    ? (item.priorytet ? '⭐' : '')
                    : (item as any)[col.key] || '-';
                  // Special handling for średnica column - 2.85 or 2.85mm should be red
                  const cellColor = col.key === 'srednica' && value && String(value).includes('2.85') ? '#dc3545' : col.color;
                  // Multi-line support for kolor/barwnik/doz_procent (multiple colors per product)
                  const isMultiLine = (col.key === 'kolor' || col.key === 'barwnik' || col.key === 'doz_procent') && typeof value === 'string' && value.includes('\n');
                  const isBarwnik = col.key === 'barwnik';
                  // Stan barwnika oznaczony przez człowieka (w „Zapotrzebowaniu na barwnik" albo
                  // klikiem w tę komórkę). Brak barwnika jest czerwony i ma pierwszeństwo —
                  // zgłoszony problem „brak barwnika" znaczy dokładnie to samo.
                  const dyeMissing = isBarwnik && (dyeProblem || item.barwnik_status === 'missing');
                  const barwnikDelivered = isBarwnik && !dyeMissing
                    && (item.barwnik_status === 'available' || !!item.barwnik_dostarczony);
                  return (
                    <td
                      key={col.key}
                      onClick={isBarwnik ? (e) => { e.stopPropagation(); onBarwnikClick(e, item); } : undefined}
                      style={{
                        ...scrollableCellStyle(col),
                        ...(cellColor && { color: cellColor }),
                        ...(col.bold && { fontWeight: 'bold' }),
                        ...((col as any).fontSize && { fontSize: (col as any).fontSize }),
                        ...((col.wrap || isMultiLine) && { whiteSpace: 'normal', lineHeight: '1.4' }),
                        ...(isPriority && { textAlign: 'center' as const, padding: '10px 2px' }),
                        // "Brak barwnika" – podświetlamy tylko tę komórkę (czerwony, najwyższy priorytet)
                        ...(dyeMissing && { backgroundColor: '#f8d7da', fontWeight: 'bold' as const }),
                        // Barwnik dostarczony do linii – komórka zielona
                        ...(barwnikDelivered && { backgroundColor: '#c3e6cb', fontWeight: 'bold' as const }),
                        ...(isBarwnik && { cursor: 'pointer' }),
                      }}
                      title={isBarwnik
                        ? (dyeMissing
                            ? t('myProduction.barwnikMissingTooltip')
                            : barwnikDelivered
                              ? t('myProduction.barwnikAvailableTooltip')
                              : t('myProduction.barwnikDeliveredTooltip'))
                        : value}
                    >
                      {isMultiLine ? (
                        value.split('\n').map((line: string, i: number) => (
                          <div key={i} style={{ borderBottom: i < value.split('\n').length - 1 ? '1px solid #dee2e6' : 'none', padding: '2px 0' }}>
                            {line}
                          </div>
                        ))
                      ) : (
                        value
                      )}
                    </td>
                  );
                })}

                {/* DO PRODUKCJI */}
                <td
                  style={{
                    ...stickyCellStyle('do_produkcji', '#fbbf24', true),
                    whiteSpace: 'nowrap',
                    fontWeight: 'bold',
                    textAlign: 'center',
                  }}
                  title={item.do_produkcji || '-'}
                >
                  {item.do_produkcji || '-'}
                </td>

                {/* ŁĄCZNA WAGA — do_produkcji * waga jednostkowa (kg) */}
                {showTotalWeightColumn && (() => {
                  const unitKg = parseWeightKg(item.waga);
                  const totalKg = parseQty(item.do_produkcji) * unitKg;
                  return (
                    <td
                      style={{
                        ...stickyCellStyle('total_weight', '#fde68a'),
                        whiteSpace: 'nowrap',
                        fontWeight: 'bold',
                        fontSize: '13px',
                        textAlign: 'center',
                      }}
                      title={unitKg > 0
                        ? `${item.do_produkcji || 0} x ${formatKg(unitKg)} kg = ${formatKg(totalKg)} kg`
                        : t('table.totalWeightUnknown')}
                    >
                      {unitKg > 0 ? formatKg(totalKg) : '-'}
                    </td>
                  );
                })()}

                {/* PRODUCED */}
                <td
                  style={{
                    ...stickyCellStyle('wyprodukowano', '#e5e7eb'),
                    whiteSpace: 'nowrap',
                    fontWeight: 'bold',
                    textAlign: 'center',
                  }}
                  title={`${labelOverrides?.produced ?? t('myProduction.produced')}: ${wyprodukowano} / ${doProdukcji}`}
                >
                  {wyprodukowano}
                </td>

                {/* POZOSTAŁO */}
                <td
                  style={{
                    ...stickyCellStyle('pozostalo', '#3b82f6'),
                    whiteSpace: 'nowrap',
                    fontWeight: '600',
                    textAlign: 'center',
                    color: 'white',
                  }}
                  title={`${t('myProduction.remaining')}: ${pozostalo}`}
                >
                  {pozostalo}
                </td>

                {/* Z1/Z2 — ukryte na liniach dużych szpul (ilość wpisuje się na stacji przewijania) */}
                {!hideQuantityColumns && (
                  <>
                {/* Z1 */}
                <td
                  onClick={(e) => {
                    e.stopPropagation();
                    onQuantityClick(e, item, 'zmiana_1');
                  }}
                  style={{
                    ...stickyCellStyle('zmiana_1', locked ? '#d3d3d3' : '#e7f3ff'),
                    cursor: locked ? 'not-allowed' : 'pointer',
                    fontWeight: item.zmiana_1 ? 'bold' : 'normal',
                    fontSize: '18px',
                    whiteSpace: 'nowrap',
                    textAlign: 'center',
                  }}
                  title={quantityTitle(t('myProduction.clickToEnterQuantityZ1'))}
                >
                  <div style={{ opacity: locked ? 0.6 : 1 }}>
                    {item.zmiana_1 || '0'}
                  </div>
                </td>

                {/* Z2 */}
                <td
                  onClick={(e) => {
                    e.stopPropagation();
                    onQuantityClick(e, item, 'zmiana_2');
                  }}
                  style={{
                    ...stickyCellStyle('zmiana_2', locked ? '#d3d3d3' : '#e7f3ff'),
                    cursor: locked ? 'not-allowed' : 'pointer',
                    fontWeight: item.zmiana_2 ? 'bold' : 'normal',
                    fontSize: '18px',
                    whiteSpace: 'nowrap',
                    textAlign: 'center',
                  }}
                  title={quantityTitle(t('myProduction.clickToEnterQuantityZ2'))}
                >
                  <div style={{ opacity: locked ? 0.6 : 1 }}>
                    {item.zmiana_2 || '0'}
                  </div>
                </td>
                  </>
                )}

                {/* UWAGI */}
                {!hideNotesColumn && (() => {
                  const hasNote = !!(item.pivot?.notes || item.uwagi);
                  return (
                    <td
                      onClick={(e) => { e.stopPropagation(); onNotesClick(e, item); }}
                      className={hasNote ? 'notes-cell-warning' : undefined}
                      style={{
                        ...commonCellStyle,
                        position: 'sticky' as const,
                        right: 0,
                        zIndex: 1,
                        backgroundColor: hasNote
                          ? '#fde047'
                          : completedLook ? '#c3e6cb' : status === 'in_progress' ? '#ffeaa7' : '#f8f9fa',
                        width: narrowColumnWidth,
                        minWidth: narrowColumnWidth,
                        maxWidth: narrowColumnWidth,
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        textAlign: 'center',
                        padding: '5px',
                      }}
                      title={item.pivot?.notes || item.uwagi || t('myProduction.clickToAddNote')}
                    >
                      {hasNote ? (
                        <span className="notes-icon-warning" style={{ fontSize: '22px' }}>
                          ⚠️
                        </span>
                      ) : (
                        <span style={{ fontSize: '18px', color: '#9ca3af' }}>
                          ➕
                        </span>
                      )}
                    </td>
                  );
                })()}
              </tr>
            );

            }); // end forEach
            return rows;
          })()}
        </tbody>
      </table>
    </div>
  );
};

export default ProductionTable;
